import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useSocket } from '../contexts/SocketContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { LogOut, CheckCircle, Clock, Moon, Sun, ChefHat } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CookDashboard() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { socket } = useSocket();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [showMenuItems, setShowMenuItems] = useState(false);
  const [updatingOrderIds, setUpdatingOrderIds] = useState({});
  const [optimisticStatuses, setOptimisticStatuses] = useState({});
  const optimisticStatusesRef = useRef({});

  useEffect(() => {
    optimisticStatusesRef.current = optimisticStatuses;
  }, [optimisticStatuses]);

  useEffect(() => {
    if (user?.role !== 'cook' && user?.role !== 'admin') {
      navigate('/login');
      return;
    }
    fetchOrders();
    fetchMenuItems();
  }, [user, navigate]);

  useEffect(() => {
    if (!socket) return;

    const handleNewOrder = () => {
      fetchOrders();
      toast.info('New order received!');
    };

    const handleOrderUpdate = (updatedOrder) => {
      setOptimisticStatuses((prev) => {
        if (!prev[updatedOrder.id] || prev[updatedOrder.id] === updatedOrder.status) {
          const next = { ...prev };
          delete next[updatedOrder.id];
          return next;
        }
        return prev;
      });

      setOrders((prev) => {
        const exists = prev.some((o) => o.id === updatedOrder.id);
        if (!exists && updatedOrder.status !== 'completed' && updatedOrder.status !== 'cancelled') {
          return [...prev, updatedOrder];
        }
        return prev
          .map((o) => (o.id === updatedOrder.id ? updatedOrder : o))
          .filter((o) => o.status !== 'completed' && o.status !== 'cancelled');
      });
    };

    const handleReconnect = () => {
      fetchOrders();
    };

    socket.on('connect', handleReconnect);
    socket.on('new_order', handleNewOrder);
    socket.on('order_updated', handleOrderUpdate);
    socket.on('queue_recalculated', handleReconnect);

    return () => {
      socket.off('connect', handleReconnect);
      socket.off('new_order', handleNewOrder);
      socket.off('order_updated', handleOrderUpdate);
      socket.off('queue_recalculated', handleReconnect);
    };
  }, [socket]);

  useEffect(() => {
    if (!user) return;
    const intervalId = setInterval(() => {
      fetchOrders();
    }, 2000);

    return () => clearInterval(intervalId);
  }, [user]);

  async function fetchOrders() {
    try {
      const response = await axios.get(`${API}/orders`);
      const activeOrders = response.data.filter(
        (o) => o.status !== 'completed' && o.status !== 'cancelled'
      );

      const serverById = Object.fromEntries(activeOrders.map((o) => [o.id, o]));

      const currentOptimistic = optimisticStatusesRef.current;

      setOrders(
        activeOrders
          .map((order) => currentOptimistic[order.id] ? { ...order, status: currentOptimistic[order.id] } : order)
          .filter((order) => currentOptimistic[order.id] !== 'completed')
      );

      setOptimisticStatuses((prev) => {
        const next = { ...prev };
        Object.entries(prev).forEach(([orderId, expectedStatus]) => {
          const serverOrder = serverById[orderId];
          if ((serverOrder && serverOrder.status === expectedStatus) || (!serverOrder && expectedStatus === 'completed')) {
            delete next[orderId];
          }
        });
        return next;
      });
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    }
  }

  const fetchMenuItems = async () => {
    try {
      const response = await axios.get(`${API}/menu-items`);
      setMenuItems(response.data);
    } catch (error) {
      console.error('Failed to fetch menu items:', error);
    }
  };

  const toggleItemAvailability = async (itemId, currentStatus) => {
    try {
      await axios.patch(`${API}/menu-items/${itemId}/availability?available=${!currentStatus}`);
      toast.success(`Item marked as ${!currentStatus ? 'available' : 'unavailable'}`);
      fetchMenuItems();
    } catch (error) {
      toast.error('Failed to update item availability');
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    const previousOrders = orders;
    setUpdatingOrderIds((prev) => ({ ...prev, [orderId]: true }));
    setOptimisticStatuses((prev) => ({ ...prev, [orderId]: status }));

    setOrders((prev) => {
      if (status === 'completed') {
        return prev.filter((o) => o.id !== orderId);
      }
      return prev.map((o) => (o.id === orderId ? { ...o, status } : o));
    });

    try {
      await axios.put(`${API}/orders/${orderId}/status`, { status });
      toast.success(`Order marked as ${status}`);
    } catch (error) {
      setOrders(previousOrders);
      setOptimisticStatuses((prev) => {
        const next = { ...prev };
        delete next[orderId];
        return next;
      });
      toast.error('Failed to update order status');
    } finally {
      setUpdatingOrderIds((prev) => ({ ...prev, [orderId]: false }));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new':
        return 'bg-amber-500';
      case 'cooking':
        return 'bg-blue-500';
      case 'ready':
        return 'bg-green-500';
      default:
        return 'bg-slate-500';
    }
  };

  const newOrders = orders.filter((o) => o.status === 'new');
  const cookingOrders = orders.filter((o) => o.status === 'cooking');
  const readyOrders = orders.filter((o) => o.status === 'ready');

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <div className="bg-white dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div>
            <h1 className="font-sans text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-50" data-testid="cook-dashboard-title">
              Kitchen Display
            </h1>
            <p className="text-slate-600 dark:text-slate-400">Welcome, {user?.name}</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setShowMenuItems(!showMenuItems)}
              variant="outline"
              data-testid="toggle-menu-items-button"
            >
              <ChefHat className="w-4 h-4 mr-2" />
              Menu Items
            </Button>
            <Button
              onClick={toggleTheme}
              variant="outline"
              size="icon"
              data-testid="cook-theme-toggle"
            >
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Button
              onClick={() => {
                logout();
                navigate('/login');
              }}
              variant="outline"
              data-testid="cook-logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {/* Menu Items Section */}
        {showMenuItems && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Menu Item Availability</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {menuItems.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                    data-testid={`menu-availability-${item.title.toLowerCase().replace(/\\s+/g, '-')}`}
                  >
                    <div className="flex-1">
                      <h3 className="font-semibold">{item.title}</h3>
                      <Badge
                        variant={item.available ? 'default' : 'destructive'}
                        className="mt-1"
                      >
                        {item.available ? 'Available' : 'Unavailable'}
                      </Badge>
                    </div>
                    <Button
                      onClick={() => toggleItemAvailability(item.id, item.available)}
                      variant={item.available ? 'destructive' : 'default'}
                      size="sm"
                      data-testid={`toggle-availability-${item.title.toLowerCase().replace(/\\s+/g, '-')}`}
                    >
                      {item.available ? 'Mark Unavailable' : 'Mark Available'}
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">New Orders</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-amber-600" data-testid="new-orders-count">{newOrders.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Cooking</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600" data-testid="cooking-orders-count">{cookingOrders.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Ready</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600" data-testid="ready-orders-count">{readyOrders.length}</div>
            </CardContent>
          </Card>
        </div>

        {/* Orders Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {orders.map((order) => {
            const timeDiff = Date.now() - new Date(order.created_at).getTime();
            const minutesAgo = Math.floor(timeDiff / 60000);

            return (
              <Card
                key={order.id}
                className={`border-2 ${order.status === 'new' ? 'border-amber-500' : order.status === 'cooking' ? 'border-blue-500' : 'border-green-500'}`}
                data-testid={`cook-order-${order.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-mono text-lg font-bold" data-testid={`cook-order-id-${order.id}`}>
                        #{order.order_number}    {/* This shows clean numbers like #1001 */}
                      </p>
                      <div className="flex items-center gap-1 text-sm text-slate-600 dark:text-slate-400">
                        <Clock className="w-3 h-3" />
                        <span>{minutesAgo} min ago</span>
                      </div>
                    </div>
                    <Badge className={getStatusColor(order.status)} data-testid={`cook-order-status-${order.id}`}>
                      {order.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 mb-4">
                    {order.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="font-medium">{item.title}</span>
                        <span className="font-bold">x{item.quantity}</span>
                      </div>
                    ))}
                  </div>
                  <div className="space-y-2">
                    {order.status === 'new' && (
                      <Button
                        onClick={() => updateOrderStatus(order.id, 'cooking')}
                        disabled={Boolean(updatingOrderIds[order.id])}
                        className="w-full bg-blue-600 hover:bg-blue-700"
                        data-testid={`start-cooking-${order.id}`}
                      >
                        {updatingOrderIds[order.id] ? 'Updating...' : 'Start Cooking'}
                      </Button>
                    )}
                    {order.status === 'cooking' && (
                      <Button
                        onClick={() => updateOrderStatus(order.id, 'ready')}
                        disabled={Boolean(updatingOrderIds[order.id])}
                        className="w-full bg-green-600 hover:bg-green-700"
                        data-testid={`mark-ready-${order.id}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        {updatingOrderIds[order.id] ? 'Updating...' : 'Mark Ready'}
                      </Button>
                    )}
                    {order.status === 'ready' && (
                      <Button
                        onClick={() => updateOrderStatus(order.id, 'completed')}
                        disabled={Boolean(updatingOrderIds[order.id])}
                        className="w-full"
                        data-testid={`mark-completed-${order.id}`}
                      >
                        {updatingOrderIds[order.id] ? 'Updating...' : 'Complete'}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {orders.length === 0 && (
          <div className="text-center py-16" data-testid="no-orders-message">
            <p className="text-slate-500 dark:text-slate-400 text-lg">No active orders</p>
          </div>
        )}
      </div>
    </div>
  );
}
